import { t, Trans } from '@lingui/macro';
import React, { FunctionComponent, FormEvent, useEffect, useState } from 'react';

import { useFetch } from '@api/ApiHelper';
import { ApiDatumResponse } from '@api/v4/ApiResponseTypes';
import { PrimaryButton } from '@components/library/button';
import { DialogSizes, StandardDialog } from '@components/library/dialog';

import classes from '../styles/create-api-key-modal.module.scss';

import { ApiKeyFormValues, emptyApiKeyFormValues, KEY_NAME_PATTERN, validateKeyName } from './shared/apiKeyForm';
import { ApiKeyFormFields } from './shared/ApiKeyFormFields';
import { GeneratedKeyPanel } from './shared/GeneratedKeyPanel';

type ModalStep = 'form' | 'generated';

interface CreateApiKeyModalProps {
  onCreated?: () => void;
  open: boolean;
  organizationKey: string;
  setOpen: (open: boolean) => void;
}

interface CreateApiKeyCreatedByUser {
  email: string;
  id: number;
  name: string;
  user_key: string;
}

/** JSON:API attributes from POST /api/v4/organizations/:key/api_keys */
interface CreateApiKeyAttributes {
  created_at: string;
  created_by_user: CreateApiKeyCreatedByUser;
  description: string | null;
  expires_at: string;
  last_used_at: string | null;
  name: string;
  revoked_at: string | null;
  rotated_from: string | null;
  source: string;
  status: string;
  token: string;
  token_prefix: string;
}

type CreateApiKeyResponse = ApiDatumResponse<CreateApiKeyAttributes, 'api_key'>;

interface CreateApiKeyRequestBody {
  description?: string;
  expires_at?: string;
  name: string;
}

export const CreateApiKeyModal: FunctionComponent<CreateApiKeyModalProps> = ({
  onCreated,
  open,
  organizationKey,
  setOpen,
}) => {
  const [values, setValues] = useState<ApiKeyFormValues>(emptyApiKeyFormValues);
  const [nameError, setNameError] = useState<string>('');
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [step, setStep] = useState<ModalStep>('form');
  const [generatedToken, setGeneratedToken] = useState('');

  const createApiKeyOptions = {
    fetchOnMount: false,
    method: 'POST' as const,
    url: `/api/v4/organizations/${organizationKey}/api_keys`,
  };

  const createApiKey = useFetch<CreateApiKeyResponse>(createApiKeyOptions);
  const isCreating = createApiKey.loading;

  const showGeneratedKey = (name: string, token: string): void => {
    setGeneratedToken(token);
    setStep('generated');

    Notify.create({
      title: t`API Key ${name} created successfully`,
      type: 'success',
    });
    onCreated?.();
  };

  const resetModalState = (): void => {
    setValues(emptyApiKeyFormValues);
    setNameError('');
    setSubmitAttempted(false);
    setStep('form');
    setGeneratedToken('');
    createApiKey.reset();
  };

  useEffect(() => {
    if (!open) {
      resetModalState();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!createApiKey.response) return;
    const attributes = createApiKey.response?.data?.attributes;
    if (!attributes?.token) return;

    showGeneratedKey(attributes.name, attributes.token);
    // Clear fetch state so Strict Mode / re-renders don't re-handle the same response.
    createApiKey.reset();
  }, [createApiKey.response]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!createApiKey.error) return;

    Notify.create({
      body: createApiKey.error.message || t`Please try again or contact support`,
      title: t`Error creating API Key`,
      type: 'error',
    });
  }, [createApiKey.error]);

  const isSubmitDisabled = !values.name.trim() || !!validateKeyName(values.name) || isCreating;

  const handleNameChange = (name: string): void => {
    setValues((prev) => ({ ...prev, name }));

    if (submitAttempted) {
      setNameError(validateKeyName(name));
      return;
    }

    const trimmed = name.trim();
    if (!trimmed) {
      setNameError(t`Name is required`);
    } else if (!KEY_NAME_PATTERN.test(trimmed)) {
      setNameError(t`Name can only contain letters, numbers, hyphens, underscores, and spaces`);
    } else {
      setNameError('');
    }
  };

  const handleDescriptionChange = (description: string): void => {
    setValues((prev) => ({ ...prev, description }));
  };

  const handleExpiryChange = (expiresAt: string): void => {
    setValues((prev) => ({ ...prev, expiryDate: expiresAt }));
  };

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setSubmitAttempted(true);

    const error = validateKeyName(values.name);
    setNameError(error);
    if (error) {
      return;
    }

    const body: CreateApiKeyRequestBody = {
      name: values.name.trim(),
      ...(values.description.trim() && { description: values.description.trim() }),
      // eslint-disable-next-line @typescript-eslint/naming-convention
      ...(values.expiryDate && { expires_at: values.expiryDate }),
    };

    createApiKey.fetch({
      ...createApiKeyOptions,
      body,
    });
  };

  const isGeneratedStep = step === 'generated';

  return (
    <StandardDialog
      dialogBodyClassName={classes.createApiKeyModalBody}
      dialogHeaderClassName={classes.createApiKeyModalHeader}
      id="create-api-key-modal"
      open={open}
      setOpen={setOpen}
      showClose={!isGeneratedStep}
      showFooter={false}
      size={DialogSizes.Auto}
      title={isGeneratedStep ? t`New API Key Generated` : t`Create API Key`}
      width={520}
    >
      {isGeneratedStep ? (
        <GeneratedKeyPanel onDone={(): void => setOpen(false)} token={generatedToken} />
      ) : (
        <form className={classes.form} onSubmit={handleSubmit}>
          <p className={classes.securityNotice}>
            <Trans>
              API keys provide privileged access to data in your Brandfolder Account.
              Never share your API secret keys in public places like GitHub, frontend code, etc.
            </Trans>
          </p>

          <ApiKeyFormFields
            idPrefix="api-key"
            nameError={nameError}
            onDescriptionChange={handleDescriptionChange}
            onExpiryChange={handleExpiryChange}
            onNameChange={handleNameChange}
            values={values}
          />

          <div className={classes.actions}>
            <PrimaryButton
              className={classes.submitButton}
              disabled={isSubmitDisabled}
              isLoading={isCreating}
              loadingCopy={t`Creating...`}
              size="medium"
              type="submit"
            >
              <Trans>Create New Key</Trans>
            </PrimaryButton>
          </div>
        </form>
      )}
    </StandardDialog>
  );
};
